import { format } from 'date-fns'
import pino from 'pino'
import sjson from 'secure-json-parse'

const LEVELS = {
  10: 'TRACE',
  20: 'DEBUG',
  30: 'INFO',
  40: 'WARN',
  50: 'ERROR',
  60: 'FATAL'
}

export enum LogCategories {
  AUTH = 'AUTH',
  STUDIO = 'STUDIO',
  NETWORK = 'NETWORK',
  ROUTING = 'ROUTING',
  REGISTRY = 'REGISTRY',
  MOBX = 'MOBX'
}

type LogObject = {
  category?: string
  data?: unknown
  level: number
  time: string
  msg: string
}

/**
 * Logger
 *
 * The minimum log level for production is warn (40)
 * In development we have access to all log levels,
 * but browsers seem to default to not showing debug and trace.
 * You can enable them by checking verbose in the levels dropdown above the console.
 *
 * Recommend using info for development logs and anything above for prod logs
 *
 * Params:
 *
 * category: The general category for the log
 * data: Any kind of data to be appended to the end of the log in json string format
 *
 * Please always include a category and use the LOG_CATEGORIES enum
 */
export const log = pino({
  level: process.env.ENV === 'prod' && process.env.NODE_ENV === 'production' ? 'warn' : 'trace',
  timestamp: pino.stdTimeFunctions.isoTime,
  browser: {
    asObject: true,
    write: {
      trace: (o: LogObject) => console.trace(prettifier(o)),
      debug: (o: LogObject) => console.debug(prettifier(o)),
      info: (o: LogObject) => console.info(prettifier(o)),
      warn: (o: LogObject) => console.warn(prettifier(o)),
      error: (o: LogObject) => console.error(prettifier(o))
    }
  }
})

export const useLogger = () => {
  return { log }
}

function prettifier(inputData: string | LogObject) {
  let logObject: LogObject

  if (typeof inputData === 'string') {
    logObject = sjson.parse(inputData)
  } else if (isObject(inputData)) {
    logObject = inputData
  }
  if (!logObject) return inputData

  const level = logObject.level
  const formattedTime = format(new Date(logObject.time), 'kk:mm:ss:SSS')
  const msg = logObject.msg
  const category = logObject.category !== undefined ? `[${logObject.category}]` : ''
  const data = logObject.data !== undefined ? `data = ${JSON.stringify(logObject.data)}` : ''

  return `${formattedTime} [${LEVELS[level]}] ${category} ${msg} - ${data}`
}

const isObject = (input: LogObject) => {
  return Object.prototype.toString.apply(input) === '[object Object]'
}
